import { useEffect, useRef, useState } from 'react';
import { Mic, X } from 'lucide-react';
import { DUO_CHARACTERS } from '../lib/duoCharacters';

/**
 * DuoStage — overlay of an AI character that "sings along" with the user.
 *
 * - The character image is rendered as a 9:16-ish portrait card next to the
 *   live camera preview.
 * - A Web Audio analyser listens to the user's microphone and drives a subtle
 *   mouth-open / scale-pulse animation on the character (vocal amplitude →
 *   visual amplitude) so it feels like they're singing along.
 * - CSS keyframes add idle breathing + sway for personality even when silent.
 */
export default function DuoStage({ stream, character, onClose }) {
  const [level, setLevel] = useState(0); // 0..1 audio amplitude
  const rafRef = useRef(null);
  const ctxRef = useRef(null);

  useEffect(() => {
    if (!stream) return undefined;
    const audioTracks = stream.getAudioTracks();
    if (!audioTracks.length) return undefined;

    let cancelled = false;
    try {
      const Ctor = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctor();
      ctxRef.current = ctx;
      const source = ctx.createMediaStreamSource(new MediaStream(audioTracks));
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);

      const tick = () => {
        if (cancelled) return;
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / data.length);
        setLevel(Math.min(1, rms * 6));
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (err) {
      console.warn('[DuoStage] analyser init failed', err);
    }
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafRef.current);
      try { ctxRef.current?.close(); } catch (err) {
        console.warn('[DuoStage] AudioContext close failed:', err?.message || err);
      }
    };
  }, [stream]);

  if (!character) return null;

  // Drive subtle visual feedback from the audio level:
  //  - tiny scale pulse on the whole portrait
  //  - "mouth" overlay opacity (a dark stripe that thins when user speaks loud)
  const pulseScale = 1 + level * 0.025;
  const mouthOpen = 0.15 + level * 0.85;
  const mouthHeight = 4 + level * 22;

  return (
    <div
      className="absolute top-3 left-3 md:top-4 md:left-4 w-32 md:w-44 rounded-2xl overflow-hidden shadow-2xl shadow-pink-500/40 ring-2 ring-white/15"
      data-testid="duo-stage"
    >
      <div className="relative aspect-[3/4] bg-black">
        <img
          src={character.img}
          alt={character.name}
          className="absolute inset-0 w-full h-full object-cover transition-transform"
          style={{
            transform: `scale(${pulseScale})`,
            animation: 'duo-sway 4s ease-in-out infinite',
          }}
          draggable={false}
        />
        {/* "Mouth" stripe overlay — sits where the lower-third of the face is */}
        <div
          className="absolute left-1/4 right-1/4 bg-black/55 rounded-full transition-all duration-75"
          style={{
            bottom: '22%',
            height: `${mouthHeight}px`,
            opacity: mouthOpen,
            filter: 'blur(2px)',
          }}
        />
        {/* Live indicator */}
        <div className="absolute top-1 left-1 flex items-center gap-1 bg-black/60 backdrop-blur-md text-white text-[9px] px-1.5 py-0.5 rounded-full font-bold">
          <Mic size={8} className="text-pink-400" />
          {character.name}
        </div>
        <button
          onClick={onClose}
          className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 backdrop-blur-md text-white flex items-center justify-center hover:bg-red-500/80"
          aria-label="Fermer le duo"
          data-testid="duo-close-btn"
        >
          <X size={11} />
        </button>
        {/* Audio level bar at the bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/40">
          <div
            className="h-full bg-gradient-to-r from-pink-500 to-cyan-400 transition-all duration-75"
            style={{ width: `${Math.round(level * 100)}%` }}
          />
        </div>
      </div>
      <style>{`
        @keyframes duo-sway {
          0%, 100% { filter: brightness(1); }
          50% { filter: brightness(1.08); }
        }
      `}</style>
    </div>
  );
}

// ---- Companion gallery ----
export function DuoGallery({ activeId, onPick }) {
  return (
    <div className="glass rounded-2xl p-5 border-2 border-pink-500/30" data-testid="duo-gallery">
      <h3 className="font-bold text-white mb-1 flex items-center gap-2">
        <Mic size={18} className="text-pink-400" /> Duo IA
        <span className="ml-auto text-[10px] bg-pink-500/20 text-pink-300 rounded-full px-2 py-0.5">NEW</span>
      </h3>
      <p className="text-xs text-zinc-400 mb-3">Choisis un perso qui chante avec toi en miroir.</p>
      <div className="grid grid-cols-4 gap-2">
        {DUO_CHARACTERS.map((c) => {
          const active = activeId === c.id;
          return (
            <button
              key={c.id}
              onClick={() => onPick(active ? null : c.id)}
              data-testid={`duo-pick-${c.id}`}
              className={`relative aspect-[3/4] rounded-xl overflow-hidden ring-2 transition-all ${
                active ? 'ring-pink-500 shadow-lg shadow-pink-500/40 scale-105' : 'ring-white/10 hover:ring-white/30'
              }`}
              title={`${c.name} • ${c.vibe}`}
            >
              <img src={c.img} alt={c.name} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/85 to-transparent p-1">
                <div className="text-[10px] font-bold text-white text-center leading-tight">{c.name}</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
